import React, {useEffect, useState} from "react";
import {BuildingData, BuildingLevelData} from "../../index";
import {aggregateBuildingLevels} from "../../Helper/AggregateBuildingLevels";
import ImageBuilding from "../ImageBuilding/ImageBuilding";
import {BuildingOverviewConfiguration} from "./index";
import {BuildingLevelRow} from "./BuildingLevelRow";
import BuildingRequirement from "./BuildingRequirement/BuildingRequirement";
import ImageMisc from "../ImageMisc/ImageMisc";
import {getEffectIcon, getEffectTranslationKey} from "./Helper/FormatEffect";
import {useTranslationContext} from "../../../react/providers/TranslationContext";
import WithTooltip from "../../../react/components/ui/withTooltip";

type Props = {
    gid: number,
    setSelectedBuilding: (gid: null|number) => void,
    getBuildingData: (gid: number) => Promise<BuildingData>,
    config: BuildingOverviewConfiguration,
}

export default function BuildingDetails({ gid, setSelectedBuilding, getBuildingData, config }: Props) {
    const [ data, setData ] = useState<null|BuildingData>(null);
    const [ levelFrom, setLevelFrom ] = useState<null|number>(null);
    const [ levelTo, setLevelTo ] = useState<null|number>(null);
    const [ levelToVolatile, setLevelToVolatile ] = useState<null|number>(null);

    const { translate } = useTranslationContext();

    useEffect(() => {
        setData(null);
        getBuildingData(gid).then(data => setData(data ?? null));
    }, [gid]);

    if (data === null) {
        return <>Loading</>;
    }

    function handleLevelSelect(level: number, volatile: boolean = false) {
        switch (true) {
            case levelFrom === null:
                setLevelFrom(level);
                break;
            case levelTo === null:
                (volatile ? setLevelToVolatile : setLevelTo)(level);
                break;
            case volatile === false:
                setLevelTo(null);
                setLevelToVolatile(null);
                setLevelFrom(null);
        }
    }

    function handleClick(level: number) {
        handleLevelSelect(level, false);
    }

    function handleMouseOver(level: number) {
        if (levelFrom !== null && levelTo === null) {
            handleLevelSelect(level, true);
        }
    }

    const myLevelFrom = levelFrom === null ? null : Math.min(levelFrom, levelTo ?? levelToVolatile ?? levelFrom);
    const myLevelTo = levelFrom === null ? null : Math.max(levelFrom, levelTo ?? levelToVolatile ?? levelFrom);

    function getLevelSelectionClassName(level: number): string {
        switch (true) {
            case level === myLevelFrom || level === myLevelTo: return 'levelSelectionBorder';
            case myLevelFrom <= level && level <= myLevelTo: return 'levelSelectionInterior';
            default: return '';
        }
    }

    const adjustedBuildingLevelData = JSON.parse(JSON.stringify(Object.values(data.levelData))) as BuildingLevelData[];
    adjustedBuildingLevelData.forEach(levelData => {
        const mainBuildingLevel = data.gid === 15 ? levelData.level - 1 : config.mainBuilding;
        const mainBuildingFactor = mainBuildingLevel === 0 ? 5 : Math.pow(0.964, mainBuildingLevel - 1);
        const buildingTimeFactor = mainBuildingFactor / config.speed;
        levelData.buildingTime = Math.round(levelData.buildingTime * buildingTimeFactor / 10) * 10;
        // Adjust speed-affected effects
        for (let i = 1; i <= 4; i++) {
            if (levelData.effects['production' + i]) {
                levelData.effects['production' + i] *= config.speed;
            }
        }
        if (levelData.effects.smallPartyTime) {
            levelData.effects.smallPartyTime /= Math.floor(config.speed / 3 + 1);
        }
        if (levelData.effects.largePartyTime) {
            levelData.effects.largePartyTime /= Math.floor(config.speed / 3 + 1);
        }
        if (levelData.effects.partyTime) {
            levelData.effects.partyTime /= (config.speed > 2 ? (config.speed / 2) : 1);
        }
        // Half WW building time
        if (data.gid === 40) {
            levelData.buildingTime = Math.round(levelData.buildingTime / 20) * 10;
        }
    });

    const aggregatedRow = aggregateBuildingLevels(
        adjustedBuildingLevelData.filter(levelData => myLevelFrom <= levelData.level && levelData.level <= myLevelTo),
        myLevelFrom === 1 ? null : adjustedBuildingLevelData[myLevelFrom - 1],
    );

    const buildingRequirements = data.prerequisites.filter(requirement => requirement.type === 'Building' || requirement.type === 'NotBuilding');
    const nonBuildingRequirements = data.prerequisites.filter(requirement => !(requirement.type === 'Building' || requirement.type === 'NotBuilding'));

    return <div className='buildingDetailContainer'>
        <div className='buildingHeader d-grid gap-5 align-items-center pb-2' style={{ gridTemplateColumns: '32px 1fr auto' }}>
            <div className='buildingTitleImage'><ImageBuilding size={32} gid={gid} /></div>
            <div className='buildingTitle'>{translate('building_g' + gid)}</div>
            <div className='buildingClose text-center' onClick={() => setSelectedBuilding(null)}>
                <svg viewBox="0 0 32 32">
                    <g className="outline">
                        <path d="M0 17.01L7.01 10 .14 3.13 3.13.14 10 7.01 17.01 0 20 2.99 12.99 10l6.87 6.87-2.99 2.99L10 12.99 2.99 20 0 17.01z"></path>
                    </g>
                    <g className="icon">
                        <path d="M0 17.01L7.01 10 .14 3.13 3.13.14 10 7.01 17.01 0 20 2.99 12.99 10l6.87 6.87-2.99 2.99L10 12.99 2.99 20 0 17.01z"></path>
                    </g>
                </svg>
            </div>
        </div>

        { data.prerequisites.length > 0 &&
            <div className='my-2'>
                <div className='mb-2'><b>{translate('requirements')}</b></div>
                <div className='buildingRequirementsContainer'>
                    <div className='d-grid gap-2' style={{ gridTemplateColumns: '1fr' }}>
                        {buildingRequirements.map((requirement, i) => <BuildingRequirement key={i} requirement={requirement} setSelectedBuilding={setSelectedBuilding} />)}
                        {nonBuildingRequirements.map((requirement, i) => <BuildingRequirement key={i} requirement={requirement} />)}
                    </div>
                </div>
            </div>
        }

        <div>
            <div className={'buildingLevelTable effectCount' + (data.effects?.length ?? 0)}>
                <div className='buildingLevelHeader buildingLevelRow'>
                    <div style={{ gridArea: 'lvl' }}>{translate('level')}</div>
                    <div style={{ gridArea: 'r1' }}><WithTooltip tooltip={translate('header_wood')}><ImageMisc type='wood' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'r2' }}><WithTooltip tooltip={translate('header_clay')}><ImageMisc type='clay' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'r3' }}><WithTooltip tooltip={translate('header_iron')}><ImageMisc type='iron' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'r4' }}><WithTooltip tooltip={translate('header_crop')}><ImageMisc type='crop' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'sum' }}><WithTooltip tooltip={translate('header_sum')}><ImageMisc type='allResources' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'pop' }}><WithTooltip tooltip={translate('header_population')}><ImageMisc type='population' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'cp' }}><WithTooltip tooltip={translate('header_culturePoints')}><ImageMisc type='culturePoints' size={24} /></WithTooltip></div>
                    <div style={{ gridArea: 'time' }}><WithTooltip tooltip={translate('header_time')}><ImageMisc type='time' size={24} /></WithTooltip></div>
                    { data.effects &&
                        data.effects.map((k, i) => <div style={{ gridArea: 'f' + i }} key={k}>
                            <WithTooltip tooltip={translate(getEffectTranslationKey(k))}>{getEffectIcon(k, true) ?? k}</WithTooltip>
                        </div>)
                    }
                </div>
                {
                    adjustedBuildingLevelData
                        .filter(levelData => Math.max(...Object.values(levelData.resourceCost)) < 10000000)
                        .map(levelData =>
                            <BuildingLevelRow
                                key={levelData.level}
                                onClick={() => handleClick(levelData.level)}
                                onMouseOver={() => handleMouseOver(levelData.level)}
                                className={'buildingLevelRowData ' + getLevelSelectionClassName(levelData.level)}
                                effects={data.effects}
                                levelData={levelData}
                                levelNode={levelData.level.toString()}
                            />
                        )
                }
                <BuildingLevelRow
                    className='aggregationRow'
                    levelNode={<ImageMisc type='sum' size={16} />}
                    levelData={aggregatedRow || { level: 0, supply: 0, effects: {}, buildingTime: 0, culturePoints: 0, resourceCost: { r1: 0, r2: 0, r3: 0, r4: 0 }, population: 0 } }
                    effects={data.effects}
                    isAggregated={true}
                />
            </div>
        </div>
    </div>
}
